import { createContext, lazy, Suspense, useContext, useState } from "react";
import { Loader2 } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  AuditDecision,
  AuditRoutingDecision,
  VendorRow,
  InfraRow,
  RevenueRow,
} from "@/api";

/**
 * Generic detail sheet — opens on the right edge with a structured view of any
 * tracked entity (routing decision, approval decision, vendor row, infra row,
 * revenue row). Reuses the existing Sheet primitive (Radix Dialog right-slide).
 *
 * Usage:
 *   const { openDetail } = useDetail();
 *   <button onClick={() => openDetail({ type: "routing", payload: routingRow })}>...</button>
 *
 * Mount <DetailSheet /> ONCE at root layout (alongside TenantSheet).
 *
 * Bundle: this shell stays in the main bundle (Context provider mounts at root).
 * The renderer component (~10 KB gzipped) is lazy-loaded on first openDetail
 * via React.lazy() — see ./DetailRenderer.tsx.
 */

export type DetailPayload =
  | { type: "routing"; payload: AuditRoutingDecision }
  | { type: "decision"; payload: AuditDecision }
  | { type: "vendor"; payload: VendorRow }
  | { type: "infra"; payload: InfraRow }
  | { type: "revenue"; payload: RevenueRow };

interface DetailCtx {
  detail: DetailPayload | null;
  openDetail: (d: DetailPayload) => void;
  closeDetail: () => void;
}

const DetailContext = createContext<DetailCtx | null>(null);

export function useDetail(): DetailCtx {
  const ctx = useContext(DetailContext);
  if (!ctx) throw new Error("useDetail must be used inside <DetailProvider>");
  return ctx;
}

export function DetailProvider({ children }: { children: React.ReactNode }) {
  const [detail, setDetail] = useState<DetailPayload | null>(null);
  return (
    <DetailContext.Provider
      value={{
        detail,
        openDetail: setDetail,
        closeDetail: () => setDetail(null),
      }}
    >
      {children}
    </DetailContext.Provider>
  );
}

// Lazy chunk — fetched the first time the user opens any detail sheet.
const DetailRenderer = lazy(() => import("@/components/DetailRenderer"));

function FallbackSpinner() {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 text-sm gap-2">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading…
    </div>
  );
}

export default function DetailSheet() {
  const { detail, closeDetail } = useDetail();
  return (
    <Sheet open={!!detail} onOpenChange={(open) => !open && closeDetail()}>
      {detail && (
        <SheetContent>
          <Suspense fallback={<FallbackSpinner />}>
            <DetailRenderer detail={detail} />
          </Suspense>
        </SheetContent>
      )}
    </Sheet>
  );
}
